(() => {
  'use strict';

  const SELECTOR =
    '[data-pn-featured-colleges]';


  function escapeHTML(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  function array(value) {
    return Array.isArray(value)
      ? value
      : [];
  }


  function cleanLabel(value) {
    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    return String(value).trim();
  }


  function unique(values) {
    return [
      ...new Set(
        values
          .map(cleanLabel)
          .filter(Boolean)
      )
    ];
  }


  function entityName(row) {
    if (!row) return '';

    return (
      row.name ||
      row.title ||
      row.label ||
      row.course_name ||
      row.exam_name ||
      ''
    );
  }


  function relationId(
    row,
    keys
  ) {
    for (const key of keys) {
      if (
        row &&
        row[key] !== undefined &&
        row[key] !== null
      ) {
        return String(row[key]);
      }
    }

    return '';
  }


  function indexById(rows) {
    const map =
      new Map();

    array(rows).forEach(row => {
      if (
        row?.id !== null &&
        row?.id !== undefined
      ) {
        map.set(
          String(row.id),
          row
        );
      }
    });

    return map;
  }


  function collegeImage(college) {
    return (
      college.image_url ||
      college.cover_image_url ||
      college.cover_image ||
      college.logo_url ||
      college.logo ||
      ''
    );
  }


  function collegeLocation(college) {
    return [
      college.city,
      college.state
    ]
      .filter(Boolean)
      .join(', ');
  }


  function collegeURL(college) {
    const slug =
      college.slug ||
      college.handle ||
      '';

    return (
      '/pages/college?view=college&college=' +
      encodeURIComponent(
        slug ||
        college.id ||
        ''
      )
    );
  }


  function compareURL(college) {
    return (
      '/pages/compare?college=' +
      encodeURIComponent(
        college.id || ''
      )
    );
  }


  function fallbackPrograms(
    college
  ) {
    const value =
      college.popular_programs ||
      college.programs ||
      college.categories ||
      [];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      return value
        .split(/[,|]/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    return [];
  }


  function fallbackExams(
    college
  ) {
    const value =
      college.entrance_exams ||
      college.exam_names ||
      [];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      return value
        .split(/[,|]/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    return [];
  }


  function buildData(
    colleges,
    collegeCourses,
    courses,
    collegeExams,
    exams
  ) {

    const coursesById =
      indexById(courses);

    const examsById =
      indexById(exams);


    return array(colleges)
      .map(college => {

        const collegeId =
          String(college.id || '');


        const programs =
          unique([
            ...array(collegeCourses)
              .filter(row =>
                String(
                  row.college_id ||
                  row.college ||
                  ''
                ) === collegeId
              )
              .map(row => {

                const courseId =
                  relationId(
                    row,
                    [
                      'course_id',
                      'program_id'
                    ]
                  );

                return (
                  entityName(
                    coursesById.get(
                      courseId
                    )
                  ) ||
                  row.course_name ||
                  row.program_name ||
                  ''
                );

              }),

            ...fallbackPrograms(
              college
            )

          ]);


        const entranceExams =
          unique([
            ...array(collegeExams)
              .filter(row =>
                String(
                  row.college_id ||
                  row.college ||
                  ''
                ) === collegeId
              )
              .map(row => {

                const examId =
                  relationId(
                    row,
                    [
                      'entrance_exam_id',
                      'exam_id'
                    ]
                  );

                return (
                  entityName(
                    examsById.get(
                      examId
                    )
                  ) ||
                  row.exam_name ||
                  ''
                );

              }),

            ...fallbackExams(
              college
            )

          ]);


        return {
          ...college,
          _programs:
            programs.slice(0, 3),

          _exams:
            entranceExams.slice(0, 3)
        };

      });
  }


  function cardHTML(college) {

    const image =
      collegeImage(college);

    const location =
      collegeLocation(college);

    const programs =
      college._programs || [];

    const exams =
      college._exams || [];

    const viewURL =
      collegeURL(college);

    const compare =
      compareURL(college);


    const tagsHTML =
      programs.length
        ? programs
            .map(program => `
              <span>
                ${escapeHTML(program)}
              </span>
            `)
            .join('')
        : `
            <span>
              ${escapeHTML(
                college.college_type ||
                'College'
              )}
            </span>
          `;


    const examText =
      exams.length
        ? exams.join(' · ')
        : 'Admissions information available';


    const imageHTML =
      image
        ? `
          <img
            src="${escapeHTML(image)}"
            alt="${escapeHTML(college.name || '')}"
            loading="lazy"
            width="800"
            height="560"
          >
        `
        : `
          <div class="pn-home-college-image-fallback">
            <span>
              ${escapeHTML(
                college.name || 'College'
              )}
            </span>
          </div>
        `;


    return `
      <article
        class="pn-college-card pn-home-college-card"
        data-college-id="${escapeHTML(
          college.id || ''
        )}"
      >

        <a
          href="${viewURL}"
          class="pn-college-card__image"
        >
          ${imageHTML}
        </a>


        <div class="pn-college-card__body">

          <h3>
            ${escapeHTML(
              college.name ||
              'College'
            )}
          </h3>


          <div class="pn-college-card__location">

            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M12 22s7-6.2 7-13A7 7 0 1 0 5 9c0 6.8 7 13 7 13Z"
              />
              <circle
                cx="12"
                cy="9"
                r="2.3"
              />
            </svg>

            <span>
              ${escapeHTML(
                location ||
                'Location available soon'
              )}
            </span>

          </div>


          <div class="pn-college-card__tags">
            ${tagsHTML}
          </div>


          <div class="pn-college-card__exam">

            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="m2 8 10-5 10 5-10 5L2 8Z"
              />
              <path
                d="M6 10.2V15c0 1.8 2.7 3.2 6 3.2s6-1.4 6-3.2v-4.8"
              />
            </svg>

            <span>
              ${escapeHTML(examText)}
            </span>

          </div>


          <div class="pn-college-card__actions">

            <a
              href="${viewURL}"
              class="pn-college-card__view"
            >
              <span>
                View College
              </span>

              <span>
                →
              </span>
            </a>


            <a
              href="${compare}"
              class="pn-college-card__compare"
            >
              <span class="pn-college-card__plus">
                +
              </span>

              <span>
                Compare
              </span>
            </a>

          </div>

        </div>

      </article>
    `;
  }


  function waitForDB(
    callback,
    attempt = 0
  ) {

    if (window.PeerNestDB) {
      callback();
      return;
    }


    if (attempt >= 60) {
      return;
    }


    setTimeout(
      () =>
        waitForDB(
          callback,
          attempt + 1
        ),
      100
    );
  }


  async function safeRequest(
    table
  ) {

    try {
      return (
        await window.PeerNestDB
          .request(
            table,
            'select=*'
          )
      ) || [];
    }

    catch (error) {

      console.warn(
        `[PeerNest] Optional ${table} data unavailable.`,
        error
      );

      return [];
    }
  }


  async function init() {

    const root =
      document.querySelector(
        SELECTOR
      );

    if (
      !root ||
      root.dataset.ready === '1'
    ) {
      return;
    }


    root.dataset.ready = '1';


    const track =
      root.querySelector(
        '[data-pnhome-track]'
      );

    const viewport =
      root.querySelector(
        '[data-pnhome-viewport]'
      );

    const prev =
      root.querySelector(
        '[data-pnhome-prev]'
      );

    const next =
      root.querySelector(
        '[data-pnhome-next]'
      );

    const dots =
      root.querySelector(
        '[data-pnhome-dots]'
      );


    if (
      !track ||
      !viewport
    ) {
      return;
    }


    try {

      const [
        colleges,
        collegeCourses,
        courses,
        collegeExams,
        exams
      ] =
        await Promise.all([

          window.PeerNestDB
            .getPublishedColleges(),

          safeRequest(
            'college_courses'
          ),

          safeRequest(
            'courses'
          ),

          safeRequest(
            'college_entrance_exams'
          ),

          safeRequest(
            'entrance_exams'
          )

        ]);


      const items =
        buildData(
          colleges,
          collegeCourses,
          courses,
          collegeExams,
          exams
        );


      if (!items.length) {

        track.innerHTML = `
          <div class="pn-home-colleges__empty">
            No published colleges yet.
          </div>
        `;

        return;
      }


      track.innerHTML =
        items
          .map(cardHTML)
          .join('');


      const originals = [
        ...track.children
      ];


      /*
        Duplicate one complete set.
        The carousel resets after traversing
        the original width, producing a
        continuous circular loop.
      */

      const originalCount =
        originals.length;


      /*
        Add enough cloned sets to guarantee that
        the viewport always has cards ahead of it,
        even when the database currently contains
        only a few published colleges.
      */

      const cloneSets =
        Math.max(
          1,
          Math.ceil(
            4 /
            originalCount
          )
        );


      for (
        let set = 0;
        set < cloneSets;
        set += 1
      ) {

        originals.forEach(card => {

          const clone =
            card.cloneNode(true);

          clone.setAttribute(
            'aria-hidden',
            'true'
          );

          clone.dataset.clone =
            '1';

          track.appendChild(
            clone
          );

        });

      }


      if (dots) {

        dots.innerHTML =
          items
            .map(
              (_, index) =>
                `<span${
                  index === 0
                    ? ' class="is-active"'
                    : ''
                }></span>`
            )
            .join('');

      }


      /*
        =====================================================
        TRUE CONTINUOUS INFINITE CAROUSEL
        =====================================================
      */

      let offset = 0;

      


      

      let animationFrame = null;
      let lastTime = 0;

      let interacting = false;
      let pointerStartX = null;

      /*
        Pixels per second.
        Lower = calmer.
        Higher = faster.
      */
      const speed = 22;


      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;


      function cardStep() {

        const card =
          track.querySelector(
            '.pn-home-college-card'
          );

        if (!card) {
          return 0;
        }


        const style =
          getComputedStyle(track);

        const gap =
          parseFloat(
            style.columnGap ||
            style.gap ||
            '0'
          ) || 0;


        return (
          card
            .getBoundingClientRect()
            .width
          +
          gap
        );

      }


      function originalLoopWidth() {

        return (
          cardStep() *
          originalCount
        );

      }


      function normalizeOffset() {

        const width =
          originalLoopWidth();

        if (!width) {
          return;
        }


        while (
          offset >= width
        ) {
          offset -= width;
        }


        while (
          offset < 0
        ) {
          offset += width;
        }

      }


      function updateDots() {

        if (!dots) {
          return;
        }


        const step =
          cardStep();

        if (!step) {
          return;
        }


        const active =
          Math.floor(
            (
              offset +
              step * 0.45
            ) /
            step
          )
          %
          originalCount;


        dots
          .querySelectorAll('span')
          .forEach(
            (dot, index) => {

              dot.classList.toggle(
                'is-active',
                index === active
              );

            }
          );

      }


      function render() {

        normalizeOffset();


        track.style.transition =
          'none';

        track.style.transform =
          `translate3d(${-offset}px, 0, 0)`;


        updateDots();

      }


      function animate(time) {

        if (!lastTime) {
          lastTime = time;
        }


        const elapsed =
          Math.min(
            time - lastTime,
            50
          );


        lastTime = time;


        if (
          !reducedMotion &&
          !interacting
        ) {

          offset += speed * (elapsed / 1000);


          render();

        }


        animationFrame =
          requestAnimationFrame(
            animate
          );

      }


      function jumpOne(
        direction
      ) {

        const step =
          cardStep();

        if (!step) {
          return;
        }


        offset +=
          step *
          direction;


        normalizeOffset();

        render();


        lastTime =
          performance.now();

      }


      next?.addEventListener(
        'click',
        () => {
          jumpOne(1);
        }
      );


      prev?.addEventListener(
        'click',
        () => {
          jumpOne(-1);
        }
      );


      /*
        Drag/swipe support.
        Continuous animation pauses only while
        the user is actively touching/dragging.
      */

      viewport.addEventListener(
        'pointerdown',
        event => {

          interacting = true;

          pointerStartX =
            event.clientX;

        }
      );


      viewport.addEventListener(
        'pointerup',
        event => {

          if (
            pointerStartX !== null
          ) {

            const delta =
              event.clientX -
              pointerStartX;


            if (
              Math.abs(delta) >
              45
            ) {

              jumpOne(
                delta < 0
                  ? 1
                  : -1
              );

            }

          }


          pointerStartX = null;

          interacting = false;

          lastTime =
            performance.now();

        }
      );


      viewport.addEventListener(
        'pointercancel',
        () => {

          pointerStartX = null;

          interacting = false;

          lastTime =
            performance.now();

        }
      );


      /*
        Keyboard accessibility:
        pause while someone is interacting
        through focus.
      */

      root.addEventListener(
        'focusin',
        () => {
          interacting = true;
        }
      );


      root.addEventListener(
        'focusout',
        () => {

          interacting = false;

          lastTime =
            performance.now();

        }
      );


      window.addEventListener(
        'resize',
        () => {

          normalizeOffset();

          render();

          lastTime =
            performance.now();

        }
      );


      /*
        Initial render.
      */

      render();


      if (!reducedMotion) {

        animationFrame =
          requestAnimationFrame(
            animate
          );

      }


      console.log(
        `[PeerNest] Homepage carousel loaded ${originalCount} published colleges from Supabase.`
      );

    }

    catch (error) {

      console.error(
        '[PeerNest] Homepage colleges failed:',
        error
      );


      track.innerHTML = `
        <div class="pn-home-colleges__empty">
          Colleges couldn't be loaded right now.
          <a href="/pages/colleges">
            Explore all colleges →
          </a>
        </div>
      `;

    }

  }


  waitForDB(init);

})();


